(() => {
  const initKey = (root) => {
    if (!root || root.dataset.wkReady === "true") return;
    root.dataset.wkReady = "true";

    const cards = [...root.querySelectorAll("[data-wk-card]")];
    const markers = [...root.querySelectorAll("[data-wk-marker]")];
    const grid = root.querySelector("[data-wk-grid]");
    const search = root.querySelector("[data-wk-search]");
    const searchForm = root.querySelector("[data-wk-search-form]");
    const suggestions = root.querySelector("[data-wk-suggestions]");
    const searchClear = root.querySelector("[data-wk-search-clear]");
    const selectionClear = root.querySelector("[data-wk-clear-selection]");
    const resultCount = root.querySelector("[data-wk-result-count]");
    const mapStatus = root.querySelector("[data-wk-map-status]");
    const noResults = root.querySelector("[data-wk-no-results]");
    const tooltip = root.querySelector("[data-wk-tooltip]");
    const map = root.querySelector(".wk-model-map");
    const zoomIn = root.querySelector("[data-wk-zoom-in]");
    const zoomOut = root.querySelector("[data-wk-zoom-out]");
    const zoomReset = root.querySelector("[data-wk-zoom-reset]");
    const zoomLevel = root.querySelector("[data-wk-zoom-level]");
    let showNumbers = root.dataset.showNumbers === "true";
    let appliedQuery = "";
    let activeNumber = null;
    let suggestionIndex = -1;
    const mapPointers = new Map();
    const baseViewBox = { x: 0, y: 0, width: 923.68, height: 1000 };
    let mapViewBox = { ...baseViewBox };
    let lastPointer = null;
    let pinchDistance = 0;
    let pinchCentre = null;
    let mapMoved = false;
    let gestureFrame = 0;
    let suppressMapClickUntil = 0;

    const pad = (value) => String(value).padStart(3, "0");

    const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

    const renderMapViewBox = () => {
      if (!map) return;
      map.setAttribute("viewBox", `${mapViewBox.x} ${mapViewBox.y} ${mapViewBox.width} ${mapViewBox.height}`);
      const scale = baseViewBox.width / mapViewBox.width;
      const scaleLabel = `${Math.round(scale * 100)}%`;
      if (zoomLevel && zoomLevel.textContent !== scaleLabel) zoomLevel.textContent = scaleLabel;
      const atBaseScale = scale <= 1.001;
      if (zoomOut && zoomOut.disabled !== atBaseScale) zoomOut.disabled = atBaseScale;
      if (zoomReset && zoomReset.disabled !== atBaseScale) zoomReset.disabled = atBaseScale;
    };

    const screenPointToMap = (clientX, clientY) => {
      if (!map) return { x: baseViewBox.width / 2, y: baseViewBox.height / 2 };
      const point = map.createSVGPoint();
      point.x = clientX;
      point.y = clientY;
      const matrix = map.getScreenCTM();
      return matrix ? point.matrixTransform(matrix.inverse()) : { x: baseViewBox.width / 2, y: baseViewBox.height / 2 };
    };

    const constrainMapViewBox = () => {
      mapViewBox.x = clamp(mapViewBox.x, baseViewBox.x, baseViewBox.width - mapViewBox.width);
      mapViewBox.y = clamp(mapViewBox.y, baseViewBox.y, baseViewBox.height - mapViewBox.height);
    };

    const setMapZoom = (nextScale, clientX, clientY) => {
      if (!map) return;
      const currentScale = baseViewBox.width / mapViewBox.width;
      const scale = clamp(nextScale, 1, 5);
      const focus = screenPointToMap(clientX, clientY);
      const ratio = currentScale / scale;
      mapViewBox = {
        x: focus.x - (focus.x - mapViewBox.x) * ratio,
        y: focus.y - (focus.y - mapViewBox.y) * ratio,
        width: baseViewBox.width / scale,
        height: baseViewBox.height / scale,
      };
      constrainMapViewBox();
      renderMapViewBox();
    };

    const zoomMapBy = (factor, clientX, clientY) => {
      if (!map) return;
      const bounds = map.getBoundingClientRect();
      const x = clientX ?? bounds.left + bounds.width / 2;
      const y = clientY ?? bounds.top + bounds.height / 2;
      setMapZoom((baseViewBox.width / mapViewBox.width) * factor, x, y);
    };

    const resetMapZoom = () => {
      mapViewBox = { ...baseViewBox };
      renderMapViewBox();
    };

    const panMapBy = (screenX, screenY) => {
      if (!map || baseViewBox.width / mapViewBox.width <= 1.001) return;
      const matrix = map.getScreenCTM();
      if (!matrix) return;
      mapViewBox.x -= screenX / matrix.a;
      mapViewBox.y -= screenY / matrix.d;
      constrainMapViewBox();
      renderMapViewBox();
    };

    const pointerDistance = (left, right) => Math.hypot(right.clientX - left.clientX, right.clientY - left.clientY);
    const pointerCentre = (left, right) => ({ clientX: (left.clientX + right.clientX) / 2, clientY: (left.clientY + right.clientY) / 2 });

    const mapPointFromMatrix = (clientX, clientY, matrix) => {
      const point = map.createSVGPoint();
      point.x = clientX;
      point.y = clientY;
      return point.matrixTransform(matrix.inverse());
    };

    const processGestureFrame = () => {
      gestureFrame = 0;
      if (!map || mapPointers.size === 0) return;
      const points = [...mapPointers.values()];
      if (points.length >= 2) {
        const left = points[0];
        const right = points[1];
        const nextDistance = pointerDistance(left, right);
        const nextCentre = pointerCentre(left, right);
        const matrix = map.getScreenCTM();
        if (matrix && pinchCentre && pinchDistance > 0) {
          const currentScale = baseViewBox.width / mapViewBox.width;
          const requestedScale = currentScale * (nextDistance / pinchDistance);
          const nextScale = clamp(requestedScale, 1, 5);
          const scaleFactor = nextScale / currentScale;
          const anchor = mapPointFromMatrix(pinchCentre.clientX, pinchCentre.clientY, matrix);
          const moveX = nextCentre.clientX - pinchCentre.clientX;
          const moveY = nextCentre.clientY - pinchCentre.clientY;
          mapViewBox = {
            x: anchor.x - (anchor.x - mapViewBox.x) / scaleFactor - moveX / (matrix.a * scaleFactor),
            y: anchor.y - (anchor.y - mapViewBox.y) / scaleFactor - moveY / (matrix.d * scaleFactor),
            width: baseViewBox.width / nextScale,
            height: baseViewBox.height / nextScale,
          };
          constrainMapViewBox();
          renderMapViewBox();
          if (Math.abs(nextDistance - pinchDistance) > 1 || Math.abs(moveX) + Math.abs(moveY) > 1) mapMoved = true;
        }
        pinchDistance = nextDistance;
        pinchCentre = nextCentre;
        lastPointer = null;
        return;
      }

      const point = points[0];
      if (!lastPointer) {
        lastPointer = point;
        return;
      }
      const moveX = point.clientX - lastPointer.clientX;
      const moveY = point.clientY - lastPointer.clientY;
      if (Math.abs(moveX) + Math.abs(moveY) > 4) mapMoved = true;
      if (point.pointerType === "touch" && baseViewBox.width / mapViewBox.width <= 1.001) {
        window.scrollBy(0, -moveY);
      } else if (moveX || moveY) {
        panMapBy(moveX, moveY);
      }
      lastPointer = point;
    };

    const scheduleGestureFrame = () => {
      if (!gestureFrame) gestureFrame = window.requestAnimationFrame(processGestureFrame);
    };

    const updateTooltip = (number) => {
      if (!tooltip) return;
      if (activeNumber && String(number) !== activeNumber) return;
      const card = cards.find((item) => item.dataset.number === String(number));
      if (!card) return;
      tooltip.querySelector(".wk-tooltip-number").textContent = pad(card.dataset.number);
      tooltip.querySelector("strong").textContent = card.querySelector("h3").textContent;
      tooltip.querySelector("small").textContent = `${card.dataset.height}m · ${card.dataset.area} · position ${pad(card.dataset.number)}`;
    };

    const setActive = (number) => {
      activeNumber = String(number);
      cards.forEach((card) => card.classList.toggle("is-active", card.dataset.number === activeNumber));
      markers.forEach((marker) => marker.classList.toggle("is-active", marker.dataset.number === activeNumber));
      map?.classList.add("has-selection");
      if (selectionClear) selectionClear.hidden = false;
      if (searchClear) searchClear.hidden = false;
      updateTooltip(activeNumber);
      const card = cards.find((item) => item.dataset.number === activeNumber);
      if (mapStatus && card) mapStatus.textContent = `Position ${pad(activeNumber)} selected · ${card.querySelector("h3").textContent}`;
    };

    const clearSelection = ({ clearSearch = false, refresh = true } = {}) => {
      activeNumber = null;
      cards.forEach((card) => card.classList.remove("is-active"));
      markers.forEach((marker) => marker.classList.remove("is-active"));
      map?.classList.remove("has-selection");
      if (selectionClear) selectionClear.hidden = true;
      if (tooltip) {
        tooltip.querySelector(".wk-tooltip-number").textContent = "001";
        tooltip.querySelector("strong").textContent = "Choose a position";
        tooltip.querySelector("small").textContent = "Tap any summit point to reveal its fell below.";
      }
      if (clearSearch) {
        appliedQuery = "";
        if (search) search.value = "";
      }
      if (searchClear) searchClear.hidden = !(search?.value || activeNumber);
      if (refresh) applyFilters();
    };

    const applyFilters = () => {
      const query = appliedQuery;
      const visible = [];
      cards.forEach((card) => {
        const numberMatch = card.dataset.number === query || pad(card.dataset.number).includes(query);
        const textMatch = card.dataset.name.includes(query) || numberMatch;
        const match = !query || textMatch;
        card.hidden = !match;
        if (match) visible.push(card);
      });

      visible.sort((left, right) => Number(left.dataset.number) - Number(right.dataset.number));
      visible.forEach((card) => grid.append(card));

      const visibleNumbers = new Set(visible.map((card) => card.dataset.number));
      const numberLabels = showNumbers || visible.length <= 24;
      markers.forEach((marker) => {
        marker.classList.toggle("is-filtered", !visibleNumbers.has(marker.dataset.number));
        marker.classList.toggle("is-numbered", numberLabels && visibleNumbers.has(marker.dataset.number));
      });
      if (resultCount) resultCount.textContent = String(visible.length);
      if (mapStatus) {
        const activeCard = cards.find((card) => card.dataset.number === activeNumber && !card.hidden);
        mapStatus.textContent = activeCard
          ? `Position ${pad(activeNumber)} selected · ${activeCard.querySelector("h3").textContent}`
          : `${visible.length} ${visible.length === 1 ? "position" : "positions"}`;
      }
      if (noResults) noResults.hidden = visible.length !== 0;
    };

    const resetFilters = () => {
      appliedQuery = "";
      if (search) search.value = "";
      clearSelection({ refresh: false });
      if (searchClear) searchClear.hidden = true;
      applyFilters();
    };

    const revealCard = (number, toggle = false) => {
      const card = cards.find((item) => item.dataset.number === String(number));
      if (!card) return;
      if (toggle && activeNumber === String(number)) {
        clearSelection();
        return;
      }
      if (card.hidden) {
        appliedQuery = card.dataset.name;
        if (search) search.value = card.querySelector("h3").textContent;
        applyFilters();
      }
      setActive(number);
      card.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const matchingCards = (value) => {
      const query = value.trim().toLowerCase();
      if (!query) return [];
      return cards
        .filter((card) => card.dataset.name.includes(query) || card.dataset.number === query || pad(card.dataset.number).includes(query))
        .sort((left, right) => {
          const leftStarts = left.dataset.name.startsWith(query) ? 0 : 1;
          const rightStarts = right.dataset.name.startsWith(query) ? 0 : 1;
          return leftStarts - rightStarts || left.dataset.name.localeCompare(right.dataset.name, "en-GB");
        });
    };

    const closeSuggestions = () => {
      if (suggestions) {
        suggestions.hidden = true;
        suggestions.replaceChildren();
      }
      suggestionIndex = -1;
      search?.setAttribute("aria-expanded", "false");
    };

    const chooseSuggestion = (card) => {
      if (!card || !search) return;
      search.value = card.querySelector("h3").textContent;
      appliedQuery = card.dataset.name;
      closeSuggestions();
      applyFilters();
      setActive(card.dataset.number);
      card.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const renderSuggestions = () => {
      if (!suggestions || !search) return;
      const matches = matchingCards(search.value).slice(0, 8);
      suggestions.replaceChildren();
      suggestionIndex = -1;
      if (!search.value.trim() || matches.length === 0) {
        closeSuggestions();
      } else {
        matches.forEach((card) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "wk-search-suggestion";
          button.setAttribute("role", "option");
          button.dataset.number = card.dataset.number;
          const number = document.createElement("b");
          number.textContent = pad(card.dataset.number);
          const name = document.createElement("strong");
          name.textContent = card.querySelector("h3").textContent;
          const detail = document.createElement("small");
          detail.textContent = `${card.dataset.height}m · ${card.dataset.area}`;
          button.append(number, name, detail);
          button.addEventListener("click", () => chooseSuggestion(card));
          suggestions.append(button);
        });
        suggestions.hidden = false;
        search.setAttribute("aria-expanded", "true");
      }
      if (searchClear) searchClear.hidden = !search.value && !activeNumber;
    };

    const executeSearch = () => {
      if (!search) return;
      const query = search.value.trim().toLowerCase();
      closeSuggestions();
      if (!query) {
        resetFilters();
        return;
      }
      const matches = matchingCards(query);
      appliedQuery = query;
      if (activeNumber && !matches.some((card) => card.dataset.number === activeNumber)) clearSelection({ refresh: false });
      applyFilters();
      if (matches.length) {
        setActive(matches[0].dataset.number);
        matches[0].scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        clearSelection({ refresh: false });
      }
    };

    search?.addEventListener("input", renderSuggestions);
    search?.addEventListener("keydown", (event) => {
      const options = [...(suggestions?.querySelectorAll(".wk-search-suggestion") || [])];
      if ((event.key === "ArrowDown" || event.key === "ArrowUp") && options.length) {
        event.preventDefault();
        suggestionIndex = event.key === "ArrowDown"
          ? (suggestionIndex + 1) % options.length
          : (suggestionIndex - 1 + options.length) % options.length;
        options.forEach((option, index) => option.classList.toggle("is-focused", index === suggestionIndex));
      } else if (event.key === "Enter" && suggestionIndex >= 0) {
        event.preventDefault();
        options[suggestionIndex]?.click();
      } else if (event.key === "Escape") {
        closeSuggestions();
        if (activeNumber) clearSelection();
      }
    });
    searchForm?.addEventListener("submit", (event) => {
      event.preventDefault();
      executeSearch();
    });
    searchClear?.addEventListener("click", resetFilters);
    selectionClear?.addEventListener("click", () => clearSelection());
    document.addEventListener("click", (event) => {
      if (!root.contains(event.target) || !event.target.closest(".wk-search-combobox")) closeSuggestions();
    });

    markers.forEach((marker) => {
      marker.addEventListener("click", (event) => {
        if (mapMoved || performance.now() < suppressMapClickUntil) {
          event.preventDefault();
          return;
        }
        revealCard(marker.dataset.number, true);
      });
      marker.addEventListener("mouseenter", () => updateTooltip(marker.dataset.number));
      marker.addEventListener("focus", () => updateTooltip(marker.dataset.number));
      marker.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          revealCard(marker.dataset.number, true);
        }
      });
    });

    cards.forEach((card) => {
      card.querySelector("[data-wk-locate]")?.addEventListener("click", () => {
        if (activeNumber === card.dataset.number) clearSelection();
        else setActive(card.dataset.number);
        root.querySelector(".wk-map-panel")?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    });

    zoomIn?.addEventListener("click", () => zoomMapBy(1.45));
    zoomOut?.addEventListener("click", () => zoomMapBy(1 / 1.45));
    zoomReset?.addEventListener("click", resetMapZoom);
    map?.addEventListener("dblclick", (event) => {
      event.preventDefault();
      zoomMapBy(1.65, event.clientX, event.clientY);
    });
    map?.addEventListener("pointerdown", (event) => {
      const markerTarget = event.target.closest?.("[data-wk-marker]");
      (markerTarget || map).setPointerCapture?.(event.pointerId);
      mapPointers.set(event.pointerId, {
        clientX: event.clientX,
        clientY: event.clientY,
        pointerType: event.pointerType,
        markerNumber: markerTarget?.dataset.number || null,
      });
      if (mapPointers.size === 1) mapMoved = false;
      if (mapPointers.size === 1) {
        lastPointer = { clientX: event.clientX, clientY: event.clientY, pointerType: event.pointerType };
        map.classList.add("is-dragging");
      } else if (mapPointers.size === 2) {
        const points = [...mapPointers.values()];
        pinchDistance = pointerDistance(points[0], points[1]);
        pinchCentre = pointerCentre(points[0], points[1]);
        lastPointer = null;
        mapMoved = true;
      }
    });
    map?.addEventListener("pointermove", (event) => {
      if (!mapPointers.has(event.pointerId)) return;
      event.preventDefault();
      const previous = mapPointers.get(event.pointerId);
      mapPointers.set(event.pointerId, {
        clientX: event.clientX,
        clientY: event.clientY,
        pointerType: event.pointerType,
        markerNumber: previous.markerNumber,
      });
      scheduleGestureFrame();
    }, { passive: false });
    const endMapPointer = (event) => {
      const endedPointer = mapPointers.get(event.pointerId);
      const endedAsTap = mapPointers.size === 1 && !mapMoved && endedPointer?.markerNumber;
      mapPointers.delete(event.pointerId);
      if (mapPointers.size === 1) {
        const point = [...mapPointers.values()][0];
        lastPointer = point;
        pinchDistance = 0;
        pinchCentre = null;
      } else if (mapPointers.size === 0) {
        if (mapMoved || endedAsTap) suppressMapClickUntil = performance.now() + 240;
        lastPointer = null;
        pinchDistance = 0;
        pinchCentre = null;
        map?.classList.remove("is-dragging");
        if (endedAsTap) revealCard(endedPointer.markerNumber, true);
        window.setTimeout(() => { mapMoved = false; }, 260);
      }
    };
    map?.addEventListener("pointerup", endMapPointer);
    map?.addEventListener("pointercancel", endMapPointer);
    map?.addEventListener("lostpointercapture", endMapPointer);
    map?.addEventListener("click", (event) => {
      if (!mapMoved && performance.now() >= suppressMapClickUntil && activeNumber && !event.target.closest(".wk-marker")) clearSelection();
    });
    root.querySelector("[data-wk-reset-filters]")?.addEventListener("click", resetFilters);
    root.querySelector("[data-wk-print]")?.addEventListener("click", () => window.print());
    root.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && activeNumber) {
        clearSelection();
      }
    });

    renderMapViewBox();
    applyFilters();
  };

  const initAll = (scope = document) => scope.querySelectorAll("[data-wainwright-key]").forEach(initKey);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => initAll());
  else initAll();
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
